/**
 * Operational section page (OR-1147).
 *
 * Data comes from the real backend (`/v2/staffing-ratios/`) via
 * `useOperationalStaffing`. RN / LPN / CNA staffing are editable inputs;
 * Total + the state-average columns are server-derived (read-only) and
 * render blank until the backend populates them.
 *
 * Layout mirrors Loss Runs:
 *   header (title + dates)
 *   card with year tabs (button-minimal strip on md+, Select on
 *     mobile) + "+ Add Row" + the table
 *   Save & Continue footer
 *
 * Add Row creates a new facility on the shared SoT (`useFacilities`)
 * so the new row also appears in Schedule, Regulatory, etc. The
 * staffing-ratio row pinned to (facility, active year) materializes on
 * first cell edit — see the table component.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import type { Key } from "react-aria-components";
import { useNavigate } from "react-router";
import { AlertCircle, Calendar, RefreshCw02 } from "@untitledui/icons";
import { Button, EmptyState, Select, Tabs } from "@tricura/atlas-ui";
import { SubmissionFooter } from "../../../components/submission-footer";
import { useFacilities } from "../../../hooks/use-facilities";
import { useOperationalStaffing } from "../../../hooks/use-operational-staffing";
import type { CatalogFacility } from "../../../api";
import type { FacilityWritePayload } from "../../../types/facility";
import { useSubmissionContext } from "../context";
import { getNextSection } from "../sections";
import { StaffingRatiosTable } from "./sections/staffing-ratios-table";
import { operationalYears } from "./year-range";

const SECTION_ID = "operational";

/** Catalog facility (autocomplete pick) → the SoT location write payload,
 *  so a new Operational row links the existing facility (and propagates to
 *  Schedule, Regulatory, etc.). Mirrors the Regulatory Add-Row flow. */
function catalogToFacilityPayload(cf: CatalogFacility): FacilityWritePayload {
    return {
        catalogFacilityId: cf.id,
        name: cf.name,
        state: cf.state ?? "",
        providerNumber: cf.cms_number ?? "",
        address: cf.address ?? "",
        city: cf.city ?? "",
        zip: cf.zip_code ?? "",
    };
}

/** A not-yet-saved Operational row. Facility MUST be picked from the
 *  catalog autocomplete (existing only); staffing values are optional and
 *  POSTed alongside the new location on Save. */
export interface OperationalDraft {
    id: string;
    facilityName: string;
    catalog: CatalogFacility | null;
    rnStaffing: string;
    lpnStaffing: string;
    cnaStaffing: string;
}

export function OperationalPage() {
    const { detail } = useSubmissionContext();
    const navigate = useNavigate();
    const [isSaving, setIsSaving] = useState(false);

    const renewal = detail.accountBasics.renewalDate || "";
    const expiration = detail.accountBasics.expirationDate || "";

    const { state: facilitiesState, create: createFacility, save: saveFacilities, isDirty: facilitiesDirty } = useFacilities(detail.id);
    const { state: staffingState, create: createStaffing, save: saveStaffing, isDirty: staffingDirty, reload: reloadStaffing } = useOperationalStaffing(detail.id);

    // OR-1297: re-fetch staffing on every navigation into Operational. The
    // hook caches per submission and only loads once, so facilities added or
    // deleted in Schedule (which change the backend-derived staffing rows)
    // wouldn't show up here without a manual refresh. Runs once per mount
    // (= once per navigation — the section remounts) as a background refresh,
    // skipped while there are unsaved staged edits so we don't clobber them.
    useEffect(() => {
        if (!detail.id || staffingDirty) return;
        void reloadStaffing();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [detail.id]);

    // Year tabs come from the Staffing Ratio endpoint — the distinct years it
    // returns, newest first. Until it loads (or when it returns nothing) we
    // fall back to the renewal-derived window so a brand-new submission can
    // still add rows.
    const years = useMemo(() => {
        const fromEndpoint =
            staffingState.status === "ready"
                ? [...new Set(staffingState.data.map((s) => s.year))].filter((y) => y > 0).sort((a, b) => b - a)
                : [];
        return fromEndpoint.length > 0 ? fromEndpoint : operationalYears(renewal);
    }, [staffingState, renewal]);

    const [activeYear, setActiveYear] = useState<number>(() => years[0]);
    // Keep the active tab valid as the year list resolves (loading → endpoint).
    useEffect(() => {
        if (!years.includes(activeYear)) setActiveYear(years[0]);
    }, [years, activeYear]);

    const yearItems = years.map((y) => ({ id: String(y), label: String(y) }));
    const selectYear = (key: Key | null) => {
        if (key === null) return;
        setActiveYear(Number(key));
    };

    // User-added rows (facility autocomplete) — kept apart from the loaded
    // rows until Save, mirroring the Regulatory Add-Row flow. `showErrors`
    // flips on the first Save attempt to surface the inline "pick a
    // facility" validation (never a banner).
    const [drafts, setDrafts] = useState<OperationalDraft[]>([]);
    const [showErrors, setShowErrors] = useState(false);

    const handleAddRow = () => {
        setDrafts((prev) => [
            ...prev,
            { id: crypto.randomUUID(), facilityName: "", catalog: null, rnStaffing: "", lpnStaffing: "", cnaStaffing: "" },
        ]);
    };
    const updateDraft = useCallback((id: string, patch: Partial<OperationalDraft>) => {
        setDrafts((prev) => prev.map((d) => (d.id === id ? { ...d, ...patch } : d)));
    }, []);
    const pickDraftFacility = useCallback((id: string, cf: CatalogFacility) => {
        setDrafts((prev) => prev.map((d) => (d.id === id ? { ...d, catalog: cf, facilityName: cf.name } : d)));
    }, []);
    const removeDraft = useCallback((id: string) => {
        setDrafts((prev) => prev.filter((d) => d.id !== id));
    }, []);

    const next = getNextSection(SECTION_ID);

    // Save flushes the drafts + staged collections without navigating.
    // Continue advances (the footer guards against leaving while dirty).
    const onSave = useCallback(async () => {
        // Each draft must reference an existing facility picked from the
        // catalog — block + show the inline error otherwise.
        if (drafts.some((d) => !d.catalog)) {
            setShowErrors(true);
            return;
        }
        setIsSaving(true);
        try {
            // Create the picked facilities (+ their staffing) on the shared
            // SoT, then flush both staged collections.
            for (const d of drafts) {
                const cf = d.catalog!;
                await createFacility(catalogToFacilityPayload(cf));
                if (d.rnStaffing || d.lpnStaffing || d.cnaStaffing) {
                    await createStaffing({
                        facilityId: cf.id,
                        // FE-only identity so the new row shows its facility
                        // before the next load (the SoT is the staffing list).
                        facilityName: cf.name,
                        address: cf.address ?? undefined,
                        city: cf.city ?? undefined,
                        state: cf.state ?? undefined,
                        year: activeYear,
                        rnStaffing: d.rnStaffing || undefined,
                        lpnStaffing: d.lpnStaffing || undefined,
                        cnaStaffing: d.cnaStaffing || undefined,
                    });
                }
            }
            setDrafts([]);
            setShowErrors(false);
            const okFacilities = await saveFacilities();
            if (!okFacilities) return;
            await saveStaffing();
        } finally {
            setIsSaving(false);
        }
    }, [drafts, createFacility, createStaffing, saveFacilities, saveStaffing, activeYear]);
    const onContinue = () => navigate(next ? `../${next.path}` : "/submissions");
    const isDirty = drafts.length > 0 || facilitiesDirty || staffingDirty;

    // Surface a system-error state when either query fails. We keep the
    // page chrome (sidebar, header) visible — only the body of the card
    // swaps for the error, matching the Figma "System Issues" mock.
    const hasError = facilitiesState.status === "error" || staffingState.status === "error";

    return (
        <div className="flex flex-col lg:min-h-0 lg:flex-1">
            <div className="flex w-full flex-col gap-6 lg:min-h-0 lg:flex-1 lg:pb-4">
                <header className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                    <div className="flex flex-col gap-1">
                        <h2 className="text-primary text-md font-semibold">Operational</h2>
                        <p className="text-tertiary text-sm">Staffing ratios</p>
                    </div>
                    <div className="flex w-full items-stretch justify-between gap-6 md:w-auto md:shrink-0 md:justify-start">
                        <DateField label="Renewal Date" value={renewal} />
                        <span aria-hidden="true" className="bg-border-secondary hidden w-px self-stretch md:block" />
                        <DateField label="Expiration Date" value={expiration} />
                    </div>
                </header>

                <section className="border-secondary bg-primary shadow-xs flex flex-col overflow-hidden rounded-xl border lg:min-h-0 lg:flex-1">
                    <header className="border-secondary flex shrink-0 items-center justify-between gap-3 border-b px-4 py-3 sm:px-6 sm:py-4">
                        <div className="hidden md:block">
                            <Tabs
                                selectedKey={String(activeYear)}
                                onSelectionChange={selectYear}
                                className="w-max"
                            >
                                <Tabs.List
                                    type="button-minimal"
                                    size="sm"
                                    aria-label="Year"
                                    items={yearItems}
                                >
                                    {(item) => <Tabs.Item {...item} />}
                                </Tabs.List>
                            </Tabs>
                        </div>
                        <div className="flex-1 md:hidden">
                            <Select
                                size="sm"
                                aria-label="Year"
                                className="w-full"
                                selectedKey={String(activeYear)}
                                onSelectionChange={selectYear}
                                items={yearItems}
                            >
                                {(item) => <Select.Item id={item.id} >{item.label}</Select.Item>}
                            </Select>
                        </div>
                        <Button color="link-gray" size="sm" onClick={handleAddRow} data-autotest="operational-add-row-button">
                            + Add Row
                        </Button>
                    </header>

                    {hasError ? (
                        <SystemErrorState onRetry={() => window.location.reload()} />
                    ) : (
                        <StaffingRatiosTable
                            submissionId={detail.id}
                            year={activeYear}
                            drafts={drafts}
                            showErrors={showErrors}
                            onDraftChange={updateDraft}
                            onDraftPickFacility={pickDraftFacility}
                            onDraftRemove={removeDraft}
                            onAddDraft={handleAddRow}
                        />
                    )}
                </section>
            </div>

            <SubmissionFooter
                onSave={onSave}
                onContinue={onContinue}
                isDirty={isDirty}
                isSaving={isSaving}
                nextSectionLabel={next?.label ?? null}
            />
        </div>
    );
}

function DateField({ label, value }: { label: string; value: string }) {
    return (
        <div className="flex items-center gap-1.5">
            <span className="text-tertiary text-xs font-normal">{label}</span>
            <span className="text-secondary inline-flex items-center gap-1.5 text-xs font-semibold">
                <Calendar aria-hidden="true" className="text-fg-quaternary size-4" />
                {value || <span className="text-placeholder font-normal">—</span>}
            </span>
        </div>
    );
}

/** Matches the Figma "Error state - System Issues" mock — an error icon
 *  in a soft circle, two lines of copy, and a single primary "Try
 *  again" CTA that reloads the page. */
function SystemErrorState({ onRetry }: { onRetry: () => void }) {
    return (
        <EmptyState size="md" className="px-6 pt-20 pb-16">
            <EmptyState.Header pattern="none">
                <EmptyState.FeaturedIcon icon={AlertCircle} color="error" theme="light" size="lg" />
            </EmptyState.Header>
            <EmptyState.Content>
                <EmptyState.Title>Something went wrong...</EmptyState.Title>
                <EmptyState.Description>
                    We had some trouble loading this page. Please refresh the page.
                </EmptyState.Description>
            </EmptyState.Content>
            <EmptyState.Footer>
                <Button color="primary" size="md" iconLeading={RefreshCw02} onClick={onRetry}>
                    Try again
                </Button>
            </EmptyState.Footer>
        </EmptyState>
    );
}

